import { orgAPI } from "@/api/api";
import {
  useDeleteSkill,
  usePutSkill,
  useSkillById,
  useSkills,
} from "@/api/skills";
import { SkillFileViewer } from "@/components/SkillFileViewer/SkillFileViewer";
import { SkillUploadDialog } from "@/components/SkillUploadDialog/SkillUploadDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSettingsInDialog } from "@/context/SettingsInDialogContext";
import { SkillDetailContent } from "@/pages/skills/SkillDetailContent";
import { downloadSkillFilesAsZip } from "@/utils/downloadSkillFilesAsZip";
import { Skill } from "@cohesive/api-client";
import { Icon, Spinner } from "@cohesive/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  ChevronLeft,
  Download,
  FileArchive,
  MoreHorizontal,
  PenLine,
  Plus,
  ScrollText,
  Trash2,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

async function downloadSkillAsZip(skill: {
  id: string;
  name: string;
}): Promise<void> {
  const files = await orgAPI.skills.listFiles(skill.id);
  await downloadSkillFilesAsZip(files, skill.name);
}

const SkillDetailPanel: React.FC<{
  skillId: string;
  onBack: () => void;
}> = ({ skillId, onBack }) => {
  const queryClient = useQueryClient();
  const { data: skill } = useSkillById(skillId);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { mutate: deleteSkill } = useDeleteSkill({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      toast.success("Skill deleted successfully");
      onBack();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete skill");
    },
  });

  const [optimisticStatus, setOptimisticStatus] = useState<string | null>(null);

  const { mutate: updateSkill, isPending: isTogglingStatus } = usePutSkill({
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      queryClient.invalidateQueries({ queryKey: ["skill", skillId] });
      toast.success(
        `Skill ${data.status === "enabled" ? "enabled" : "disabled"}`,
      );
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update skill");
      setOptimisticStatus(null);
    },
  });

  useEffect(() => {
    if (
      optimisticStatus !== null &&
      skill?.status !== undefined &&
      skill.status === optimisticStatus
    ) {
      setOptimisticStatus(null);
    }
  }, [skill?.status, optimisticStatus]);

  const effectiveStatus = optimisticStatus ?? skill?.status;
  const isEnabled = effectiveStatus === "enabled";
  const [isDownloading, setIsDownloading] = useState(false);

  const handleToggleStatus = () => {
    if (!skill) return;
    const currentStatus = optimisticStatus ?? skill.status;
    const newStatus = currentStatus === "enabled" ? "disabled" : "enabled";
    setOptimisticStatus(newStatus);
    updateSkill({ id: skill.id, data: { status: newStatus } });
  };

  const handleDownload = async () => {
    if (!skill || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadSkillAsZip(skill);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to download skill");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-0">
      <div className="flex items-center justify-between mb-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2 text-muted-foreground hover:text-foreground gap-1.5"
          onClick={onBack}
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Skills
        </Button>

        {skill && (
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-muted-foreground hover:text-foreground"
                    onClick={handleDownload}
                    disabled={isDownloading}
                  >
                    {isDownloading ? (
                      <Spinner size="xs" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  Download skill (.zip)
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-xs text-muted-foreground">
              {isTogglingStatus
                ? isEnabled
                  ? "Enabling…"
                  : "Disabling…"
                : isEnabled
                  ? "Enabled"
                  : "Disabled"}
            </span>
            {isTogglingStatus && <Spinner size="xs" />}
            <Switch
              checked={isEnabled}
              onCheckedChange={handleToggleStatus}
              disabled={isTogglingStatus}
              className="scale-90"
            />
          </div>
        )}
      </div>

      <SkillDetailContent
        mode="edit"
        skillId={skillId}
        context="pane"
        onSuccess={() => {}}
      />

      <div className="mt-6">
        <div className="border-t my-2" />
        <h3 className="text-lg font-semibold mt-4 mb-3">Skill Files</h3>
        <SkillFileViewer skillId={skillId} />
      </div>

      <div className="mt-6 pt-4 border-t">
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => setDeleteDialogOpen(true)}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Skill
        </Button>
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Skill</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{skill?.name || "this skill"}"?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (skill) deleteSkill(skill.id);
              }}
            >
              Delete Skill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const MySkills: React.FC = () => {
  const queryClient = useQueryClient();
  const isInDialog = useSettingsInDialog();

  const { data: skills = [], isLoading } = useSkills();
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [skillPendingDelete, setSkillPendingDelete] = useState<Skill | null>(
    null,
  );

  /** Defer open so DropdownMenu can finish unmounting; avoids body stuck with pointer-events: none (Radix #3317). */
  const openUploadZipDialog = useCallback(() => {
    window.setTimeout(() => setUploadDialogOpen(true), 0);
  }, []);

  /** Defer open so DropdownMenu can finish unmounting; avoids body stuck with pointer-events: none (Radix #3317). */
  const openDeleteDialog = useCallback((skill: Skill) => {
    window.setTimeout(() => setSkillPendingDelete(skill), 0);
  }, []);

  const [statusFilter, setStatusFilter] = useState<
    "all" | "enabled" | "disabled"
  >("all");
  const [downloadingSkillId, setDownloadingSkillId] = useState<string | null>(
    null,
  );

  const handleDownloadSkill = async (skill: Skill) => {
    if (downloadingSkillId) return;
    setDownloadingSkillId(skill.id);
    try {
      await downloadSkillAsZip(skill);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to download skill");
    } finally {
      setDownloadingSkillId(null);
    }
  };

  const filteredSkills =
    statusFilter === "all"
      ? skills
      : skills.filter((s) => s.status === statusFilter);

  const { mutate: updateSkill } = usePutSkill({
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      toast.success(
        `Skill ${data.status === "enabled" ? "enabled" : "disabled"}`,
      );
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update skill");
    },
  });

  const { mutate: deleteSkill, isPending: isDeletingSkill } = useDeleteSkill({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      toast.success("Skill deleted successfully");
      setSkillPendingDelete(null);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete skill");
    },
  });

  const handleSkillClick = (skill: Skill) => {
    setSelectedSkillId(skill.id);
  };

  const handleCreateClick = () => {
    setSelectedSkillId("new");
  };

  const handleCreateSuccess = (skill: Skill) => {
    setSelectedSkillId(skill.id);
  };

  if (selectedSkillId === "new") {
    return (
      <div className="flex flex-col min-h-0">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start -ml-2 mb-4 text-muted-foreground hover:text-foreground gap-1.5"
          onClick={() => setSelectedSkillId(null)}
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Skills
        </Button>
        <SkillDetailContent
          mode="create"
          context="pane"
          onSuccess={handleCreateSuccess}
        />
      </div>
    );
  }

  if (selectedSkillId) {
    return (
      <SkillDetailPanel
        key={selectedSkillId}
        skillId={selectedSkillId}
        onBack={() => setSelectedSkillId(null)}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
          >
            <SelectTrigger className="h-8 w-[110px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="enabled">Enabled</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="gap-1.5">
                  <Plus className="h-4 w-4" />
                  Add Skill
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleCreateClick}>
                  <PenLine className="h-4 w-4 mr-2" />
                  Create from scratch
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={openUploadZipDialog}>
                  <FileArchive className="h-4 w-4 mr-2" />
                  Upload a skill
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {skills.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-plum/10 to-rust/10 flex items-center justify-center mb-4">
              <ScrollText className="h-8 w-8 text-plum" />
            </div>
            <h3 className="text-lg font-semibold mb-1">No skills yet</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-xs">
              Add skills to enhance your agent's capabilities.
            </p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="gap-1.5">
                  <Plus className="h-4 w-4" />
                  Add Skill
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center">
                <DropdownMenuItem onClick={handleCreateClick}>
                  <PenLine className="h-4 w-4 mr-2" />
                  Create from scratch
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={openUploadZipDialog}>
                  <FileArchive className="h-4 w-4 mr-2" />
                  Upload a skill
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden sm:table-cell">
                    Description
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">
                    Updated
                  </TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSkills.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-sm text-muted-foreground py-8"
                    >
                      No {statusFilter} skills.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSkills.map((skill) => (
                    <TableRow
                      key={skill.id}
                      className="cursor-pointer"
                      onClick={() => handleSkillClick(skill)}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {skill.icon && skill.icon.type ? (
                            <Icon icon={skill.icon} size={16} />
                          ) : (
                            <ScrollText className="h-4 w-4 text-muted-foreground" />
                          )}
                          {skill.name}
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell max-w-[200px]">
                        <span className="text-sm text-muted-foreground truncate block">
                          {skill.description || "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            skill.status === "enabled"
                              ? "bg-lc-active/15 text-lc-active dark:text-lc-active border-lc-active/30 hover:bg-lc-active/20"
                              : "bg-lc-warning/15 text-amber-700 dark:text-lc-warning border-amber-500/30 hover:bg-lc-warning/20"
                          }
                          variant="outline"
                        >
                          {skill.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                        {skill.updated_at
                          ? formatDistanceToNow(new Date(skill.updated_at), {
                              addSuffix: true,
                            })
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSkillClick(skill);
                              }}
                            >
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const newStatus =
                                  skill.status === "enabled"
                                    ? "disabled"
                                    : "enabled";
                                updateSkill({
                                  id: skill.id,
                                  data: { status: newStatus },
                                });
                              }}
                            >
                              {skill.status === "enabled"
                                ? "Disable"
                                : "Enable"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadSkill(skill);
                              }}
                              disabled={downloadingSkillId === skill.id}
                            >
                              {downloadingSkillId === skill.id
                                ? "Downloading…"
                                : "Download"}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteDialog(skill);
                              }}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog
        open={skillPendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setSkillPendingDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Skill</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "
              {skillPendingDelete?.name || "this skill"}"? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSkillPendingDelete(null)}
              disabled={isDeletingSkill}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isDeletingSkill}
              onClick={() => {
                if (skillPendingDelete) {
                  deleteSkill(skillPendingDelete.id);
                }
              }}
            >
              Delete Skill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SkillUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onSuccess={
          isInDialog ? (skill) => setSelectedSkillId(skill.id) : undefined
        }
      />
    </>
  );
};

export default MySkills;
